// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { SecureApp, useAuthContext } from "@asgardeo/auth-react";

import { useIdleTimer } from "react-idle-timer";
import React, { useContext, useEffect, useState } from "react";

import PreLoader from "@component/common/PreLoader";
import SessionWarningDialog from "@component/common/SessionWarningDialog";
import LoginScreen from "@component/ui/LoginScreen";
import {
  loadPrivileges,
  setUserAuthData,
  setAuthError,
} from "../slices/authSlice/auth";
import { RootState, useAppDispatch, useAppSelector } from "@slices/store";
import { getUserInfo } from "@slices/userSlice/user";
import { APIService } from "@utils/apiService";
import { redirectUrl } from "@config/constant";

type AuthContextType = {
  appSignIn: () => void;
  appSignOut: () => void;
};

enum AppState {
  Loading = "loading",
  Unauthenticated = "unauthenticated",
  Authenticating = "authenticating",
  Authenticated = "authenticated",
}

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

// Session timeout: 15 minutes in milliseconds
const timeout = 15 * 60 * 1000;
// Show warning 4 seconds before session timeout
const promptBeforeIdle = 4_000;

const AppAuthProvider = (props: { children: React.ReactNode }) => {
  const [sessionWarningOpen, setSessionWarningOpen] = useState<boolean>(false);
  const [appState, setAppState] = useState<AppState>(AppState.Loading);

  const dispatch = useAppDispatch();
  const auth = useAppSelector((state: RootState) => state.auth);

  const onPrompt = () => {
    appState === AppState.Authenticated && setSessionWarningOpen(true);
  };

  const { activate } = useIdleTimer({
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });

  const handleContinue = () => {
    setSessionWarningOpen(false);
    activate();
  };

  const {
    signIn,
    signOut,
    getDecodedIDToken,
    getBasicUserInfo,
    refreshAccessToken,
    isAuthenticated,
    getIDToken,
    trySignInSilently,
    getAccessToken,
    state,
  } = useAuthContext();

  useEffect(() => {
    if (!localStorage.getItem(redirectUrl)) {
      localStorage.setItem(
        redirectUrl,
        window.location.href.replace(window.location.origin, "")
      );
    }
  }, []);

  const setupAuthenticatedUser = async () => {
    const [userInfo, idToken, decodedIdToken] = await Promise.all([
      getBasicUserInfo(),
      getIDToken(),
      getDecodedIDToken(),
    ]);

    dispatch(
      setUserAuthData({
        userInfo: userInfo,
        decodedIdToken: decodedIdToken,
      })
    );

    new APIService(idToken, refreshToken);

    await dispatch(getUserInfo());
    await dispatch(loadPrivileges());
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setAppState(AppState.Loading);

        if (state.isLoading) return;

        if (state.isAuthenticated) {
          setAppState(AppState.Authenticating);
          await setupAuthenticatedUser();

          if (mounted) setAppState(AppState.Authenticated);
        } else {
          const silentSignInSuccess = await trySignInSilently();

          if (mounted)
            setAppState(
              silentSignInSuccess
                ? AppState.Authenticating
                : AppState.Unauthenticated
            );
        }
      } catch (err) {
        if (mounted) {
          dispatch(setAuthError());
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [state.isAuthenticated, state.isLoading]);

  const refreshToken = () => {
    return new Promise<{ accessToken: string }>(async (resolve) => {
      const userIsAuthenticated = await isAuthenticated();
      if (userIsAuthenticated) {
        resolve({ accessToken: await getAccessToken() });
      } else {
        refreshAccessToken()
          .then(async () => {
            const accessToken = await getAccessToken();
            resolve({ accessToken: accessToken });
          })
          .catch(() => {
            appSignOut();
          });
      }
    });
  };

  const appSignOut = async () => {
    setAppState(AppState.Loading);
    await signOut();
    setAppState(AppState.Unauthenticated);
  };

  const appSignIn = async () => {
    await signIn();
    setAppState(AppState.Loading);
  };

  const authContext: AuthContextType = {
    appSignIn: appSignIn,
    appSignOut: appSignOut,
  };

  const renderContent = () => {
    switch (appState) {
      case AppState.Loading:
        return <PreLoader isLoading message="Loading ..." />;

      case AppState.Authenticating:
        return <PreLoader isLoading message="Loading User Info ..." />;

      case AppState.Authenticated:
        return (
          <AuthContext.Provider value={authContext}>
            {props.children}
          </AuthContext.Provider>
        );

      case AppState.Unauthenticated:
        return (
          <AuthContext.Provider value={authContext}>
            <LoginScreen />
          </AuthContext.Provider>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SessionWarningDialog
        open={sessionWarningOpen}
        handleContinue={handleContinue}
        appSignOut={appSignOut}
      />

      <SecureApp
        fallback={
          <PreLoader isLoading message="We are getting things ready ..." />
        }
      >
        {renderContent()}
      </SecureApp>
    </>
  );
};

const useAppAuthContext = (): AuthContextType => useContext(AuthContext);

export { useAppAuthContext };

export default AppAuthProvider;
